import { markConnectStarted } from "@/components/connectors/pendingConnect.ts";
import {
  connectorQueries,
  deleteConnector,
  deleteCustomMcpServer,
  getReauthUrl,
  getWhatsappSyncStatus,
  removeTextMessagesPhone,
  startWhatsappSyncLink,
  triggerConnectorSync,
  unlinkWhatsappSync,
} from "@/components/connectors/api.ts";
import { ConnectorConnectAction } from "@/components/connectors/ConnectorConnectAction.tsx";
import { TeamsConnectorActions } from "@/components/connectors/teams/TeamsConnectorActions.tsx";
import { WhatsappGroupPickerDialog } from "@/components/connectors/whatsapp/WhatsappGroupPickerDialog.tsx";
import {
  CustomMcpDialog,
  type CustomMcpDialogTarget,
} from "@/components/connectors/CustomMcpDialog.tsx";
import { ImapReconnectButton } from "@/components/connectors/ImapConnectDialog.tsx";
import {
  ConnectorRow,
  ConnectorTile,
  reconnectButtonClass,
} from "@/components/connectors/ConnectorTile.tsx";
import { ConnectorLogo } from "@/components/connectors/ConnectorLogo.tsx";
import { CustomMcpLogo } from "@/components/connectors/CustomMcpLogo.tsx";
import { customMcpServerIdOf } from "@/shared/connectors/customMcp.ts";
import {
  getConnectorIcon,
  getConnectorIconForVariant,
  TEXT_MESSAGES_ICON_KEY,
} from "@/components/connectors/providerIcons.tsx";
import { TextOkthxLink } from "@/components/connectors/textMessages/TextOkthxLink.tsx";
import { useConnectorCatalog } from "@/components/connectors/useConnectorCatalog.ts";
import { connectorConnectMode } from "@/shared/connectors/connectMode.ts";
import { connectorRowLabels } from "@/shared/connectors/providers.ts";
import { formatPhoneNumber, primaryPhoneHandleValue } from "@/shared/phone.ts";
import { apiErrorMessage } from "@/shared/apiErrorMessage.ts";
import { isOtpRequired } from "@/shared/stepUp.ts";
import { TEXT_MESSAGES_NAME } from "@/shared/textMessages.ts";
import { useStepUp } from "@/lib/useStepUp.ts";
import { ChangePhoneNumberDialog } from "@/views/Settings/ChangePhoneNumberDialog.tsx";
import {
  compareConnectorsConnectedFirst,
  matchesConnectorGalleryFilter,
} from "@/shared/connectors/galleryFilter.ts";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon.tsx";
import {
  MainContentWrapper,
  SiteHeaderContent,
  SiteHeaderTitle,
  ViewWrapper,
} from "@/components/layout/ViewWrapper.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { cardListClass, galleryCardsClass } from "@/components/threads/shared.tsx";
import {
  appliedFilterLabel,
  type PanelControlAxes,
  PanelControls,
  type PanelViewMode,
  viewAxis,
} from "@/views/Settings/panelControls.tsx";
import {
  panelDescriptionClass,
  SettingsPanelHeader,
} from "@/views/Settings/SettingsPanelHeader.tsx";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { useIncrementalSync } from "@/hooks/sync.ts";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { cn } from "@/lib/utils.ts";
import { connectorCatalogItemId } from "@/types/connectorCapabilities.ts";
import {
  type ConnectorSummary,
  PHONE_ONLY_SIGN_IN,
  type TextMessagesConnector,
  WHATSAPP_CATALOG_ID,
  WHATSAPP_CATALOG_SLUG,
  type WhatsappSyncStatusResponse,
} from "@/types/connectors.ts";
import { validatePhoneNumber } from "@/types/phone.ts";
import { useGateValue } from "@statsig/react-bindings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Globe, LoaderCircle, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "wouter";
import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { PanelToolbar } from "@/components/layout/SplitPanel/PanelToolbar.tsx";

/**
 * Statsig event names for the first-party tiles, preserved from when these
 * were hardcoded here. Keyed by catalog id so the events keep their meaning
 * now that the rows come from the server.
 */
const STATSIG_NAME_BY_ID: Record<string, string> = {
  "provider:google": "connectors-add-google",
  "provider:microsoft": "connectors-add-outlook",
  "provider:zoom": "connectors-add-zoom",
  "provider:slack": "connectors-add-slack",
  "provider:teams": "connectors-add-teams",
  "provider:gdrive": "connectors-add-gdrive",
  "email_inbox:google": "connectors-add-gmail-inbox",
  "email_inbox:microsoft": "connectors-add-outlook-inbox",
  // Named per preset rather than left to the generic bucket: the whole point of
  // splitting the tiles is being able to see which providers people pick.
  "email_inbox:yahoo": "connectors-add-yahoo-inbox",
  "email_inbox:aol": "connectors-add-aol-inbox",
  "email_inbox:verizon": "connectors-add-verizon-inbox",
  "email_inbox:att": "connectors-add-att-inbox",
  "email_inbox:xfinity": "connectors-add-xfinity-inbox",
  "email_inbox:icloud": "connectors-add-icloud-inbox",
  "email_inbox:imap": "connectors-add-imap-inbox",
};

/**
 * A single browseable connector in the gallery, normalized from the two underlying
 * sources (first-party OAuth providers and admin-enabled dynamic connectors) so
 * search, filtering, and the card grid can treat them uniformly.
 */
type GalleryItem = {
  key: string;
  name: string;
  description: string;
  websiteUrl?: string;
  /** Catalog slug, or `whatsapp` on the local WhatsApp tile. Target of the
   *  `?connect=<slug>` deep link the agent hands out. */
  slug?: string;
  icon: ReactNode;
  /** The user has this connector. Drives the "Connected" filter. Stays true even
   *  when credentials expired, so a needs-reauth item doesn't vanish. */
  connected: boolean;
  /** Connected but credentials expired — show "Reconnect" instead of the badge. */
  needsReconnect: boolean;
  /** Category slugs, for the filter dropdown. Local tiles (WhatsApp) carry none,
   *  so they show only under "All". */
  categories: string[];
  /** Overrides the tile's default status badges (see ConnectorTileItem.badge).
   *  Used by account cards to show "Main" on the login account. */
  badge?: ReactNode;
  /** See ConnectorTileItem.note. */
  note?: ReactNode;
  /** See ConnectorTileItem.reconnectNote. */
  reconnectNote?: ReactNode;
  action: ReactNode;
};

/**
 * What the shared disconnect dialog is about to remove. Account cards,
 * connected dynamic-connector tiles, and the WhatsApp tile all stage one of
 * these, so every card class confirms through the same dialog: `connector`
 * runs the generic delete endpoint; `whatsapp` runs the device unlink, which
 * also drops the synced chat archive; `customMcp` removes a server the user
 * added, connection and all; `textMessages` removes the account's verified
 * phone number, unless the server would refuse it (`blockedReason`).
 */
type RemoveTarget =
  | { kind: "connector"; connectorId: number; name: string; syncsData: boolean }
  | { kind: "whatsapp"; name: string }
  | { kind: "customMcp"; serverId: number; name: string }
  | {
      kind: "textMessages";
      name: string;
      phoneNumber: string;
      blockedReason: TextMessagesConnector["removeBlockedReason"];
    };

/** What the confirm dialog warns will go, for the staged target. */
const removeDescription = (target: RemoveTarget | null): string => {
  if (target?.kind === "textMessages") {
    if (target.blockedReason === "phoneSignIn") return PHONE_ONLY_SIGN_IN.error;
    if (target.blockedReason === "otpRequired") {
      return "Confirm this number with a code first, then you can remove it.";
    }
    return `Okthx will stop texting ${formatPhoneNumber(target.phoneNumber)}, and you won't be able to sign in with it.`;
  }
  if (target?.kind === "whatsapp") {
    return "Your linked device will be logged out and its synced chat archive deleted. This cannot be undone.";
  }
  if (target?.kind === "customMcp") {
    return "The server, its connection, and what the assistant learned about it will be removed. This cannot be undone.";
  }
  if (target?.kind === "connector" && target.syncsData) {
    return "Its synced data (emails, calendar events) will be deleted. This cannot be undone.";
  }
  return "This cannot be undone.";
};

/**
 * The Connectors gallery's search box and its filter / view axes, owned in one
 * place so the settings panel and the canvas view can't offer different ones.
 *
 * Reads the catalog itself for the category rows. That is a second call to
 * `useConnectorCatalog` while the panel below makes its own; react-query serves
 * both observers from one fetch. The alternative — having the panel publish its
 * options upward — would make the header wait a render on the body it sits
 * above.
 *
 * The read is quiet (`showToasts: false`) because each catalog call also
 * subscribes to OAuth results, and the canvas runs this hook twice beside the
 * panel's own read. With toasts on, one failed connect showed three identical
 * toasts (BIG-8703). The panel's read is the one that announces.
 */
export const useConnectorsGalleryControls = () => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<string>("all");
  const [view, setView] = useState<PanelViewMode>("card");
  const { data: connectorCatalog } = useConnectorCatalog({ showToasts: false });

  const filterOptions = [
    { key: "all", label: "All" },
    { key: "connected", label: "Connected" },
    ...(connectorCatalog?.categories ?? []).map((category) => ({
      key: category.slug,
      label: category.label,
    })),
  ];

  // If the selected category left the vocabulary (an admin renamed or deleted
  // it) since it was picked, reset rather than filtering by a slug nothing has.
  // Adjusting state during render is React's own answer to a reset effect, and
  // resetting the stored value rather than deriving around it is what stops a
  // returning category from silently re-activating.
  //
  // Only once the catalog has answered. Its categories are the vocabulary, so
  // while the query is empty — first load, a refetch after eviction, an error —
  // every slug looks deleted, and resetting then would throw away a choice the
  // user can see on screen.
  if (
    connectorCatalog &&
    filter !== "all" &&
    filter !== "connected" &&
    !filterOptions.some((o) => o.key === filter)
  ) {
    setFilter("all");
  }

  const controls: PanelControlAxes = {
    filters: [
      {
        label: "Filter",
        value: filter,
        onChange: setFilter,
        options: filterOptions.map((option) => ({ value: option.key, label: option.label })),
        defaultValue: "all",
        display: "segmented",
        statsigName: "connectors-filter",
      },
    ],
    view: viewAxis({
      value: view,
      onChange: setView,
      defaultValue: "card",
      statsigName: "connectors-view",
    }),
  };

  return {
    controls,
    values: { search, filter, view },
    search: {
      value: search,
      onChange: setSearch,
      placeholder: "Search connectors",
      statsigName: "connectors-search",
    },
  };
};

/** The three values a host owns when it supplies this panel's chrome. */
export type ConnectorsGalleryControlValues = {
  search: string;
  filter: string;
  view: PanelViewMode;
};

/**
 * The connector browse-and-manage grid, header-agnostic so two surfaces can host
 * the identical experience: the standalone /connectors route (which the agent's
 * `?connect=<slug>` deep links target) and the Settings Connectors tab. All
 * gallery state and mutations live here.
 */
export const ConnectorsGalleryPanel = ({
  hideTitle = false,
  hostControls,
}: {
  hideTitle?: boolean;
  /**
   * Supplied by a host whose own action row owns the search box and the filter
   * view menus — the canvas view. When set, this renders the results only, the
   * same contract `ArtifactLibraryBody` offers. Omitted, the panel keeps its own
   * header, description, and state.
   */
  hostControls?: ConnectorsGalleryControlValues;
} = {}) => {
  const whatsappEnabled = useGateValue("whatsapp");

  // The same axes the canvas host builds, so the two surfaces cannot end up
  // offering different filters for one list — which is what this hook exists to
  // prevent, and what a second copy here quietly undid. Called unconditionally,
  // as hooks must be; a host that supplies its own values simply wins below, and
  // the unused state costs a `useState` and an already-cached catalog read.
  const ownControls = useConnectorsGalleryControls();
  const search = hostControls?.search ?? ownControls.values.search;
  const filter = hostControls?.filter ?? ownControls.values.filter;
  const view = hostControls?.view ?? ownControls.values.view;

  // Deep link from the agent's "connect X first" reply: /connectors?connect=<slug>
  // briefly highlights the matching catalog tile. Unknown slug is a silent no-op.
  const [searchParams] = useSearchParams();
  const connectSlug = searchParams.get("connect");
  const [highlightSlug, setHighlightSlug] = useState<string | null>(null);

  // The gallery's one toasting OAuth subscription; the controls' reads are quiet.
  const { data: connectorCatalog } = useConnectorCatalog();

  const teamsEnabled = useGateValue("teams");
  const textMessagesEnabled = useGateValue("text_messages_connector");
  const { userContext } = useCurrentUserContext();
  // Undefined from a server that predates the connector: no card and no tile,
  // rather than offering to add a number the account may already have.
  const textMessages = textMessagesEnabled ? userContext?.user.textMessages : undefined;
  const stepUp = useStepUp();
  const queryClient = useQueryClient();
  const { isSyncing, handleIncrementalSync } = useIncrementalSync();

  const {
    data: accountsData,
    isError: isAccountsError,
    refetch: refetchAccounts,
  } = useQuery(connectorQueries.list());

  // Ids with a delete in flight. useMutation only reports its LATEST call
  // (isPending/variables), so with two concurrent removals the first card
  // would read as idle and re-enable its trash mid-request — this set keeps
  // every in-flight removal disabled until its own request settles.
  const [pendingRemovals, setPendingRemovals] = useState<ReadonlySet<number>>(new Set());

  const deleteMutation = useMutation({
    mutationFn: deleteConnector,
    onMutate: (connectorId: number) => {
      setPendingRemovals((prev) => new Set(prev).add(connectorId));
    },
    onSettled: (_data, _error, connectorId) => {
      setPendingRemovals((prev) => {
        const next = new Set(prev);
        next.delete(connectorId);
        return next;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      queryClient.invalidateQueries({ queryKey: ["connectorCatalog"] });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      queryClient.invalidateQueries({ queryKey: ["calendars"] });
      queryClient.invalidateQueries({ queryKey: ["preferences"] });
      // Deleting the whatsapp connector runs the full device disconnect — the
      // gallery tile and assistant card read these caches.
      queryClient.invalidateQueries({ queryKey: ["whatsappSyncStatus"] });
      queryClient.invalidateQueries({ queryKey: ["whatsappConnectorStatus"] });
    },
    onError: () => {
      // The row may have vanished server-side already (deleted elsewhere, or a
      // timeout after the server committed) — refetch so a phantom card can't
      // sit behind an unactionable "try again" loop.
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      errorToast("Failed to remove connector. Please try again.");
    },
  });

  const syncMutation = useMutation({
    mutationFn: triggerConnectorSync,
    onSuccess: () => {
      confirmationToast("Sync started for this connector.");
    },
    onError: () => {
      errorToast("Failed to start sync. Please try again.");
    },
  });

  // WhatsApp's disconnect is not the generic connector delete: unlinking logs
  // the device out and drops the synced archive through its own endpoint. It
  // lives here (not in the WhatsApp action component) so the shared confirm
  // dialog below can dispatch it alongside ordinary connector deletes.
  const whatsappUnlinkMutation = useMutation({
    mutationFn: unlinkWhatsappSync,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["whatsappSyncStatus"] });
      void queryClient.invalidateQueries({ queryKey: ["whatsappConnectorStatus"] });
      // The connector row is deleted — Connected accounts reads this cache.
      void queryClient.invalidateQueries({ queryKey: ["connectors"] });
    },
    onError: () => errorToast("Couldn't disconnect WhatsApp. Try again."),
  });

  // Removing a custom server is not the generic connector delete either: the
  // server row goes, and the connection and the assistant's words for it with
  // it. It lives here so the shared confirm dialog can dispatch it.
  // Server ids with a removal in flight, for the same reason `pendingRemovals`
  // is a set: useMutation reports only its latest call.
  const [pendingServerRemovals, setPendingServerRemovals] = useState<ReadonlySet<number>>(
    new Set(),
  );
  const deleteCustomMcpMutation = useMutation({
    mutationFn: deleteCustomMcpServer,
    onMutate: (serverId: number) => {
      setPendingServerRemovals((prev) => new Set(prev).add(serverId));
    },
    onSettled: (_data, _error, serverId) => {
      setPendingServerRemovals((prev) => {
        const next = new Set(prev);
        next.delete(serverId);
        return next;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectorCatalog"] });
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["connectorCatalog"] });
      errorToast("Couldn't remove the server. Try again.");
    },
  });

  // Removing Text Messages removes the account's verified number, not a
  // connector row, so it has its own endpoint. The server's refusals carry
  // their own words; a step-up refusal is already being redirected by the
  // http interceptor and needs no toast.
  const removeTextMessagesMutation = useMutation({
    mutationFn: removeTextMessagesPhone,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      void queryClient.invalidateQueries({ queryKey: ["connectors"] });
    },
    onError: (error) => {
      if (isOtpRequired(error)) return;
      errorToast(apiErrorMessage(error) ?? "Couldn't remove Text Messages. Try again.");
    },
  });

  const [addPhoneDialogOpen, setAddPhoneDialogOpen] = useState(false);

  // Every card's trash stages its target here; the AlertDialog below asks
  // before anything is deleted. The target survives the dialog closing (only
  // the open flag flips) so the copy stays stable through the exit animation.
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const stageRemove = useCallback((target: RemoveTarget) => {
    setRemoveTarget(target);
    setRemoveDialogOpen(true);
  }, []);

  // Held here rather than in `WhatsappConnectAction`: the tile swaps that
  // action out for its disconnect button the moment the link lands, which
  // would unmount the picker with it.
  const [whatsappGroupPickerOpen, setWhatsappGroupPickerOpen] = useState(false);
  const openWhatsappGroupPicker = useCallback(() => setWhatsappGroupPickerOpen(true), []);
  const closeWhatsappGroupPicker = useCallback(() => setWhatsappGroupPickerOpen(false), []);
  // The rename dialog for one custom server. Keyed on what it edits so the
  // form reopens on the server's current name rather than the one it was
  // first mounted with.
  const [editTarget, setEditTarget] = useState<CustomMcpDialogTarget | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const stageEdit = useCallback((target: CustomMcpDialogTarget) => {
    setEditTarget(target);
    setEditDialogOpen(true);
  }, []);

  const handleRemoveAccount = useCallback(
    (connector: ConnectorSummary) => {
      stageRemove({
        kind: "connector",
        connectorId: connector.id,
        name: connector.displayName,
        // Only Google/Microsoft accounts sync data worth warning about; for
        // other providers the synced-data warning is wrong and scares users
        // off the routine disconnect/reconnect fix.
        syncsData: connector.provider === "google" || connector.provider === "microsoft",
      });
    },
    [stageRemove],
  );

  const removeConnector = deleteMutation.mutate;
  const unlinkWhatsapp = whatsappUnlinkMutation.mutate;
  const removeCustomMcp = deleteCustomMcpMutation.mutate;
  const removeTextMessages = removeTextMessagesMutation.mutate;
  const confirmRemove = useCallback(() => {
    if (removeTarget?.kind === "whatsapp") unlinkWhatsapp();
    else if (removeTarget?.kind === "customMcp") removeCustomMcp(removeTarget.serverId);
    else if (removeTarget?.kind === "textMessages") removeTextMessages();
    else if (removeTarget) removeConnector(removeTarget.connectorId);
    setRemoveDialogOpen(false);
  }, [removeTarget, removeConnector, unlinkWhatsapp, removeCustomMcp, removeTextMessages]);

  // Scoped to the STAGED target, not any in-flight removal: per-card trash
  // buttons stay enabled while another card's delete runs, so a second dialog
  // can open then — and its Cancel/Disconnect must not sit dead until the
  // unrelated request settles.
  const isRemovePending = (target: RemoveTarget | null): boolean => {
    if (target === null) return false;
    if (target.kind === "whatsapp") return whatsappUnlinkMutation.isPending;
    if (target.kind === "customMcp") return pendingServerRemovals.has(target.serverId);
    if (target.kind === "textMessages") return removeTextMessagesMutation.isPending;
    return pendingRemovals.has(target.connectorId);
  };
  const removePending = isRemovePending(removeTarget);
  const removeBlockedReason =
    removeTarget?.kind === "textMessages" ? removeTarget.blockedReason : null;
  const removeVerb = removeTarget?.kind === "customMcp" ? "Remove" : "Disconnect";
  const removeTitle =
    removeBlockedReason === "phoneSignIn"
      ? `Can't remove ${removeTarget?.name}`
      : `${removeVerb} ${removeTarget?.name}?`;

  // The visible-connector rules the delete guards count against. WhatsApp
  // rows stay in this set (the guard math must count them) but never become
  // cards — the local WhatsApp tile owns that provider's state and unlink
  // flow.
  const allVisibleConnectors = useMemo(() => {
    return [...(accountsData?.connectors ?? [])]
      .filter((a) => a.provider !== "teams" || teamsEnabled)
      .filter((a) => a.provider !== "whatsapp" || whatsappEnabled)
      .filter((a) => a.provider !== "mcp")
      .filter((a) => a.provider !== "openapi")
      .sort((a, b) => {
        if (a.isLogin && !b.isLogin) return -1;
        if (!a.isLogin && b.isLogin) return 1;
        return (a.email ?? "").localeCompare(b.email ?? ""); // null emails sort first
      });
  }, [accountsData?.connectors, teamsEnabled, whatsappEnabled]);

  // Main (login) accounts are never deletable here, by design — the card
  // simply doesn't offer it (see the !isLogin at the call site). Beyond that,
  // the only guard is not deleting your last remaining connector.
  const canDeleteSecondary = allVisibleConnectors.length > 1;

  // Account cards inherit the catalog categories of their exact role so a
  // filtered view (e.g. a calendar category) still shows which accounts are
  // connected — with the provider tile's badge suppressed, the cards are the
  // only carrier of connected state. Keyed by catalog id, not provider: Google
  // fields both a calendar item (provider:google) and an inbox item
  // (email_inbox:google), and a Gmail-inbox card must not surface under
  // calendar categories.
  const categoriesByCatalogId = useMemo(() => {
    const byId = new Map<string, string[]>();
    for (const item of connectorCatalog?.items ?? []) {
      if (item.provider === null) continue;
      byId.set(item.id, item.categories);
    }
    return byId;
  }, [connectorCatalog]);

  const syncConnector = syncMutation.mutate;
  const accountItems = useMemo<GalleryItem[]>(() => {
    return allVisibleConnectors
      .filter((connector) => connector.provider !== "whatsapp")
      .map((connector) => {
        const isEmailInbox = !connector.isLogin && connector.supportsEmail;
        const icon = getConnectorIcon(connector.provider, connector.isLogin, isEmailInbox);
        // The second line identifies the card: the connected account for
        // first-party providers, the connector kind for the admin-registered
        // long tail. Doubles as the search haystack, so accounts stay findable
        // by email.
        const { title: name, subtitle } = connectorRowLabels(connector);
        return {
          key: `account:${connector.id}`,
          name,
          description: subtitle,
          // No drawn mark means the admin-registered long tail, which
          // ConnectorLogo resolves from the service's own favicon — the same
          // mark its catalog tile carries. Ends on the account's initial rather
          // than a plug, matching native's ProviderIcon.
          icon: icon ?? (
            <ConnectorLogo
              websiteUrl={connector.websiteUrl}
              name={name}
              size="sm"
              className="shrink-0"
              fallback={<span className="text-xs font-medium">{name.charAt(0).toUpperCase()}</span>}
            />
          ),
          connected: true,
          needsReconnect: connector.needsAuth,
          categories: categoriesByCatalogId.get(connectorCatalogItemId(connector)) ?? [],
          // While reconnecting, no badge at all: the tile's ReconnectNote
          // line carries the state, and any chip beside the Reconnect button
          // squeezed the title out of the header row.
          badge:
            connector.isLogin && !connector.needsAuth ? (
              <Badge variant="brand" size="sm" className="h-6 py-0 shrink-0">
                Main account
              </Badge>
            ) : undefined,
          action: (
            <ConnectorAccountAction
              connector={connector}
              canDelete={!connector.isLogin && canDeleteSecondary}
              onRemove={handleRemoveAccount}
              // Scoped to the acting card: a delete or sync on one account
              // must not disable or spin the controls on every other card.
              isDeleting={pendingRemovals.has(connector.id)}
              onSync={syncConnector}
              isSyncing={syncMutation.isPending && syncMutation.variables === connector.id}
            />
          ),
        };
      });
  }, [
    allVisibleConnectors,
    canDeleteSecondary,
    categoriesByCatalogId,
    handleRemoveAccount,
    pendingRemovals,
    syncConnector,
    syncMutation.isPending,
    syncMutation.variables,
  ]);

  const { data: whatsappSyncStatus } = useQuery({
    queryKey: ["whatsappSyncStatus"],
    queryFn: getWhatsappSyncStatus,
    enabled: whatsappEnabled,
    // Changes only on explicit connect/disconnect actions, which invalidate
    // it; the connect dialog's fast poll runs under its own key.
    staleTime: 60_000,
  });

  // Once account rows are loaded, per-account cards own connected state for
  // first-party providers — their catalog tiles drop the Connected badge and
  // stop counting as connected for sort/filter, while the tile's action stays
  // a way to connect one more account (rendered as a plain "Connect"). While
  // loading (or on error) this stays false and tiles render exactly as before,
  // so the page never implies zero connected accounts. Dynamic connectors
  // (provider null) keep tile-owned state — their rows never become cards.
  const accountsLoaded = accountsData !== undefined;

  const catalogItems = useMemo<GalleryItem[]>(() => {
    if (!connectorCatalog) return [];
    return connectorCatalog.items.map((item) => {
      // First-party connectors ship a drawn mark; the admin-registered long
      // tail falls back to a favicon off its own site.
      const brandIcon = item.provider
        ? getConnectorIconForVariant(item.provider, item.iconVariant ?? "brand")
        : null;
      const statsigName = STATSIG_NAME_BY_ID[item.id] ?? "connectors-catalog-connect";
      const suppressTileConnected = item.provider !== null && accountsLoaded;
      // With the badge suppressed, the tile's action must not turn into a
      // competing amber "Reconnect" either — the catalog's reconnect starts a
      // fresh connect, while the account card links per-account reauth (a
      // dedicated endpoint for google/microsoft; the generic connect path for
      // the rest). Clearing `connected` on the action's copy makes
      // connectorConnectMode yield a plain "Connect" AND guarantees the
      // action can never compute `hidden` (which requires connected: true),
      // so the tile keeps an affordance no matter what flags the catalog
      // carries.
      const actionItem = suppressTileConnected ? { ...item, connected: false } : item;
      // Dynamic connectors are managed on their tile — there is no account card
      // to carry the disconnect the way first-party providers get one. The
      // catalog hands back the connected row's id precisely so the tile can
      // offer it. First-party tiles never get a trash here: their account
      // cards own removal, and doubling it would offer two delete paths to
      // the same row.
      const connectorId = item.connectorId;
      const customServerId = customMcpServerIdOf(item);
      const needsReconnect = suppressTileConnected
        ? false
        : connectorConnectMode(item).needsReconnect;
      // A server the user added is theirs to rename and remove whether or not
      // it is connected, and the tile says so with a line that only this
      // account sees it. The status chip stays the tile's default: a second
      // chip pushed the name out of the card at compact widths.
      const custom =
        customServerId !== null
          ? {
              note: "Only you can see this connector.",
              action: (
                <div className="flex items-center gap-1">
                  <ConnectorConnectAction item={actionItem} statsigName={statsigName} />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-6 shrink-0"
                        aria-label={`Rename ${item.name}`}
                        onClick={() => stageEdit({ kind: "edit", serverId: customServerId, item })}
                        data-statsig-name="connectors-custom-mcp-rename"
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Rename</TooltipContent>
                  </Tooltip>
                  <DisconnectIconButton
                    name={item.name}
                    label="Remove"
                    disabled={pendingServerRemovals.has(customServerId)}
                    onClick={() =>
                      stageRemove({ kind: "customMcp", serverId: customServerId, name: item.name })
                    }
                    statsigName="connectors-custom-mcp-remove"
                  />
                </div>
              ),
            }
          : null;
      return {
        key: `catalog:${item.id}`,
        name: item.name,
        description: item.description,
        websiteUrl: item.websiteUrl,
        icon: custom ? (
          <CustomMcpLogo name={item.name} />
        ) : (
          (brandIcon ?? <ConnectorLogo websiteUrl={item.websiteUrl} name={item.name} />)
        ),
        // A connected-but-expired connector stays in the "Connected" tab; the tile
        // shows "Reconnect" instead of the badge (see needsReconnect).
        connected: suppressTileConnected ? false : item.connected,
        needsReconnect,
        categories: item.categories,
        slug: item.slug,
        ...(custom ? { note: custom.note } : {}),
        action: custom?.action ?? (
          <div className="flex items-center gap-1">
            <ConnectorConnectAction item={actionItem} statsigName={statsigName} />
            {item.provider === null && item.connected && connectorId !== null && (
              <DisconnectIconButton
                name={item.name}
                disabled={pendingRemovals.has(connectorId)}
                onClick={() =>
                  stageRemove({
                    kind: "connector",
                    connectorId,
                    name: item.name,
                    // Dynamic connectors are live connections the assistant
                    // queries on demand — nothing synced to warn about.
                    syncsData: false,
                  })
                }
                statsigName="connectors-catalog-remove"
              />
            )}
          </div>
        ),
      };
    });
  }, [
    connectorCatalog,
    accountsLoaded,
    stageRemove,
    stageEdit,
    pendingRemovals,
    pendingServerRemovals,
  ]);

  // WhatsApp is not an OAuth provider — the connect action is a device-pairing
  // dialog (WhatsApp Linked Devices), which links the personal device AND
  // verifies the number in one step. "Connected" means the session is live.
  const whatsappItems = useMemo<GalleryItem[]>(() => {
    if (!whatsappEnabled) return [];
    return [
      {
        key: WHATSAPP_CATALOG_ID,
        slug: WHATSAPP_CATALOG_SLUG,
        name: "WhatsApp",
        description:
          "Link your WhatsApp so the assistant can message you, search your chats, and send messages from your number (only with your approval)",
        icon: <WhatsAppIcon className="w-5 h-5" />,
        connected: whatsappSyncStatus?.status === "connected",
        needsReconnect: whatsappSyncStatus?.status === "logged_out",
        categories: [],
        // Once linked, the tile manages like every other connected card: a
        // trash routed through the shared confirm dialog (unlinking deletes
        // the synced chat archive, so it must not be a one-click action).
        // The connect/reconnect flow keeps its device-pairing component.
        action:
          whatsappSyncStatus?.status === "connected" ? (
            <DisconnectIconButton
              name="WhatsApp"
              disabled={whatsappUnlinkMutation.isPending}
              onClick={() => stageRemove({ kind: "whatsapp", name: "WhatsApp" })}
              statsigName="connectors-whatsapp-unlink"
            />
          ) : (
            <WhatsappConnectAction status={whatsappSyncStatus} onLinked={openWhatsappGroupPicker} />
          ),
      },
    ];
  }, [
    whatsappEnabled,
    whatsappSyncStatus,
    whatsappUnlinkMutation.isPending,
    stageRemove,
    openWhatsappGroupPicker,
  ]);

  // Text Messages is the account's verified phone, not a connector row: a card
  // when there is a number, a tile to add one when there isn't. It stays out of
  // `allVisibleConnectors`, so it never counts toward the last-connector guard.
  // Its fix when Okthx can't text is a text from the person, not a reauth, so
  // the card offers the number to text in place of a Reconnect link.
  const textMessagesItems = useMemo<GalleryItem[]>(() => {
    if (textMessages === undefined) return [];
    const icon = getConnectorIcon(TEXT_MESSAGES_ICON_KEY, false, false);
    if (textMessages === null) {
      return [
        {
          key: "local:textMessages",
          name: TEXT_MESSAGES_NAME,
          description: "Add your phone number so Okthx can text you",
          icon,
          connected: false,
          needsReconnect: false,
          categories: [],
          action: (
            <Button
              variant="primary"
              size="xs"
              className="h-6 px-2 text-xs shrink-0"
              onClick={() => setAddPhoneDialogOpen(true)}
              data-statsig-name="connectors-add-text-messages"
            >
              Connect
            </Button>
          ),
        },
      ];
    }
    const { needsAttention } = textMessages;
    return [
      {
        key: "local:textMessages",
        name: TEXT_MESSAGES_NAME,
        description: formatPhoneNumber(textMessages.phoneNumber),
        icon,
        connected: true,
        needsReconnect: needsAttention !== null,
        ...(needsAttention !== null && {
          reconnectNote: (
            <TextMessagesReconnectNote
              reason={needsAttention}
              assistantNumber={textMessages.assistantNumber}
            />
          ),
        }),
        categories: [],
        action: (
          <div className="flex items-center gap-1">
            {needsAttention !== null && (
              <Button
                variant="primary"
                size="xs"
                className={reconnectButtonClass}
                asChild
                data-statsig-name="connectors-text-messages-reconnect"
              >
                <TextOkthxLink
                  reason={needsAttention}
                  assistantNumber={textMessages.assistantNumber}
                >
                  Reconnect
                </TextOkthxLink>
              </Button>
            )}
            <DisconnectIconButton
              name={TEXT_MESSAGES_NAME}
              disabled={removeTextMessagesMutation.isPending}
              onClick={() =>
                stageRemove({
                  kind: "textMessages",
                  name: TEXT_MESSAGES_NAME,
                  phoneNumber: textMessages.phoneNumber,
                  blockedReason: textMessages.removeBlockedReason,
                })
              }
              statsigName="connectors-text-messages-remove"
            />
          </div>
        ),
      },
    ];
  }, [textMessages, removeTextMessagesMutation.isPending, stageRemove]);

  // WhatsApp stays local: the device pairing is not an OAuth redirect, so it
  // has no catalog `connectPath`.
  // Account cards lead in their own order (login first, then email) — never
  // through the comparator, which sorts connected items alphabetically and
  // would bury the Main card ("Gmail" < "Google"). The rest float
  // connected-first, then by name — except an unconnected Text Messages tile,
  // which leads what is left to connect: reaching the person matters most.
  const allItems = useMemo(() => {
    const rest = [...textMessagesItems, ...whatsappItems, ...catalogItems].sort(
      compareConnectorsConnectedFirst,
    );
    const addText = rest.findIndex((item) => item.key === "local:textMessages" && !item.connected);
    if (addText > 0) {
      const [tile] = rest.splice(addText, 1);
      const firstToConnect = rest.findIndex((item) => !item.connected);
      rest.splice(firstToConnect === -1 ? rest.length : firstToConnect, 0, tile);
    }
    return [...accountItems, ...rest];
  }, [accountItems, textMessagesItems, whatsappItems, catalogItems]);

  // Depend on a derived boolean rather than the item array: a query refetch
  // swaps the array identity without changing content, and re-running on that
  // would restart the highlight timer and re-scroll mid-interaction.
  const connectSlugInGallery =
    connectSlug !== null && allItems.some((item) => item.slug === connectSlug);
  useEffect(() => {
    if (!connectSlug || !connectSlugInGallery) return;
    setHighlightSlug(connectSlug);
    const timeout = setTimeout(() => setHighlightSlug(null), 2500);
    return () => clearTimeout(timeout);
  }, [connectSlug, connectSlugInGallery]);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return allItems.filter((item) => {
      if (!matchesConnectorGalleryFilter(item, filter)) return false;
      if (!query) return true;
      return `${item.name} ${item.description}`.toLowerCase().includes(query);
    });
  }, [allItems, filter, search]);

  return (
    <div className="pb-12">
      {/* A host that supplies its own chrome (the canvas view) passes
          `controls` and gets the results only — the same contract
          `ArtifactLibraryBody` offers. Settings passes nothing and keeps
          the panel header and description below. */}
      {!hostControls && (
        <>
          <SettingsPanelHeader
            title="Connectors"
            hideTitle={hideTitle}
            search={ownControls.search}
            filters={
              <PanelControls
                labeled
                {...ownControls.controls}
                subject="connectors"
                statsigName="connectors-filter"
              />
            }
            mobileFilters={
              <PanelControls
                {...ownControls.controls}
                subject="connectors"
                statsigName="connectors-filter"
              />
            }
            // The trigger's label is the tool cluster's width; a change to it has
            // to re-run the header's collapse measurement.
            toolsKey={appliedFilterLabel(ownControls.controls)}
            actions={
              <Button
                variant="outline"
                size="sm"
                onClick={handleIncrementalSync}
                disabled={isSyncing}
                data-statsig-name="connectors-sync-now"
                className="shrink-0 gap-1.5!"
              >
                {/* The label stays constant so the button keeps its width; the
                  spinning icon and disabled state carry the in-flight signal. */}
                <RefreshCw
                  className={cn("size-4", isSyncing && "animate-spin")}
                  strokeWidth={2.5}
                />
                Sync all
              </Button>
            }
            mobileActions={
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label={isSyncing ? "Syncing" : "Sync all"}
                onClick={handleIncrementalSync}
                disabled={isSyncing}
                data-statsig-name="connectors-sync-now"
              >
                <RefreshCw className={cn("size-5", isSyncing && "animate-spin")} />
              </Button>
            }
          />

          <p className={panelDescriptionClass}>
            Connect your tools and accounts so Okthx can work across them.
          </p>
        </>
      )}

      {isAccountsError && (
        <div
          role="alert"
          className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground"
        >
          <span>Couldn't load your connected accounts. Check your connection and try again.</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetchAccounts()}
            data-statsig-name="connectors-accounts-retry"
          >
            Retry
          </Button>
        </div>
      )}

      <Button
        asChild
        variant="outline"
        className="mb-6"
        data-statsig-name="connectors-website-sign-ins"
      >
        <Link href="/website-sign-ins">
          <Globe className="size-4" />
          Website sign-ins
          <ArrowRight className="size-4" />
        </Link>
      </Button>
      {visibleItems.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
          {filter === "connected" && !search.trim()
            ? "You haven't connected anything yet."
            : "No connectors match your search."}
        </div>
      ) : (
        <div className={view === "card" ? galleryCardsClass : cardListClass}>
          {visibleItems.map((item) => {
            const Item = view === "card" ? ConnectorTile : ConnectorRow;
            return (
              <Item
                key={item.key}
                item={item}
                highlighted={highlightSlug !== null && item.slug === highlightSlug}
              />
            );
          })}
        </div>
      )}

      <AlertDialog
        open={removeDialogOpen}
        onOpenChange={(open) => !open && setRemoveDialogOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{removeTitle}</AlertDialogTitle>
            <AlertDialogDescription>{removeDescription(removeTarget)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {/* A number that is the only way to sign in can't go, so there is
                nothing to confirm; a number never proven by code is proven
                first, as Account settings does before changing it. */}
            {removeBlockedReason === "phoneSignIn" && (
              <AlertDialogAction data-statsig-name="connectors-text-messages-remove-refused-ok">
                OK
              </AlertDialogAction>
            )}
            {removeBlockedReason !== "phoneSignIn" && (
              <AlertDialogCancel disabled={removePending}>Cancel</AlertDialogCancel>
            )}
            {removeBlockedReason === "otpRequired" && (
              <Button asChild data-statsig-name="connectors-text-messages-remove-step-up">
                <a href={stepUp.href}>Confirm number</a>
              </Button>
            )}
            {removeBlockedReason === null && (
              <AlertDialogAction
                variant="destructive"
                onClick={confirmRemove}
                disabled={removePending}
              >
                {removeVerb}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {textMessages === null && (
        <ChangePhoneNumberDialog
          mode="add"
          open={addPhoneDialogOpen}
          onOpenChange={setAddPhoneDialogOpen}
        />
      )}
      {whatsappGroupPickerOpen && <WhatsappGroupPickerDialog onClose={closeWhatsappGroupPicker} />}
      {editTarget?.kind === "edit" && (
        <CustomMcpDialog
          key={`${editTarget.serverId}:${editTarget.item.name}:${editTarget.item.description}`}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          target={editTarget}
        />
      )}
    </div>
  );
};

/**
 * The standalone /connectors page: the panel under its own back-button
 * header. The Settings Connectors tab renders the panel without this chrome.
 * Back only, matching Settings — the page has one parent, and a hamburger
 * would be a second exit from a screen with a known place in the stack.
 */
export const ConnectorsGallery = () => (
  <ViewWrapper
    headerContent={
      <SiteHeaderContent includeBackButton hideNav>
        <SiteHeaderTitle>Connectors</SiteHeaderTitle>
      </SiteHeaderContent>
    }
  >
    <MainContentWrapper columns={14} className="flex-1 min-w-0">
      {/* The site header above already titles the page, so the panel's own
          desktop heading is suppressed. */}
      <ConnectorsGalleryPanel hideTitle />
    </MainContentWrapper>
  </ViewWrapper>
);

/**
 * The one disconnect affordance every connected card shares: a destructive
 * trash icon that stages the shared confirm dialog. Centralised so account
 * cards, dynamic-connector tiles, and the WhatsApp tile cannot drift apart —
 * the card grammar is the contract now that connectors arrive by the hundreds.
 */
const DisconnectIconButton = ({
  name,
  label = "Disconnect",
  onClick,
  disabled,
  statsigName,
}: {
  name: string;
  /** The verb: "Disconnect" drops a connection, "Remove" drops the thing itself. */
  label?: "Disconnect" | "Remove";
  onClick: () => void;
  disabled: boolean;
  statsigName: string;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="size-6 shrink-0 text-destructive hover:text-destructive"
        aria-label={`${label} ${name}`}
        disabled={disabled}
        onClick={onClick}
        data-statsig-name={statsigName}
      >
        <Trash2 className="size-3.5" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

/**
 * The manage controls on one connected-account card: a per-account Reconnect
 * link when credentials expired (reauth for THIS account, unlike the catalog
 * tile's Reconnect which starts a fresh connect), and a disconnect icon when
 * the guard rules allow it. The main (login) account never gets a disconnect —
 * callers pass canDelete accordingly.
 */
const ConnectorAccountAction = ({
  connector,
  canDelete,
  onRemove,
  isDeleting,
  onSync,
  isSyncing,
}: {
  connector: ConnectorSummary;
  canDelete: boolean;
  onRemove: (connector: ConnectorSummary) => void;
  isDeleting: boolean;
  onSync: (connectorId: number) => void;
  isSyncing: boolean;
}) => {
  // Per-account sync exists only for the providers whose backend sync fans out
  // per connector, and an expired account can't sync until it reconnects.
  const canSync =
    !connector.needsAuth && (connector.provider === "google" || connector.provider === "microsoft");
  return (
    <div className="flex items-center gap-1">
      <TeamsConnectorActions connector={connector} />
      {/* IMAP has no OAuth consent to re-run; reconnecting re-enters the app password in place. */}
      {connector.needsAuth &&
        (connector.provider === "imap" ? (
          <ImapReconnectButton connector={connector} />
        ) : (
          <Button
            variant="primary"
            size="xs"
            asChild
            className={reconnectButtonClass}
            data-statsig-name="connectors-account-reconnect"
          >
            <a
              href={getReauthUrl(connector)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={
                // Slack's channel picker, and Teams' install help while okthx
                // isn't in their Teams yet; a routine reconnect needs neither.
                connector.provider === "slack" || connector.teams?.botInstalled === false
                  ? () => markConnectStarted(connector.provider)
                  : undefined
              }
            >
              Reconnect
            </a>
          </Button>
        ))}
      {canDelete && (
        <DisconnectIconButton
          name={connector.displayName}
          disabled={isDeleting}
          onClick={() => onRemove(connector)}
          statsigName="connectors-account-remove"
        />
      )}
      {canSync && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-6 shrink-0"
              aria-label={`Sync ${connector.displayName}`}
              disabled={isSyncing}
              onClick={() => onSync(connector.id)}
              data-statsig-name="connectors-account-sync"
            >
              <RefreshCw className={cn("size-3.5", isSyncing && "animate-spin")} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Sync now</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

/**
 * WhatsApp device-link connect: link a personal WhatsApp as a device through
 * the session host, which also verifies the number — the linked session's own
 * JID is the number, so there is no code entry or verification step. The QR is
 * a genuine WhatsApp Web link code produced by our own Baileys session —
 * rendered as a QR to scan from the phone's Linked Devices screen. Completion
 * is the session flipping to `connected`, observed by polling the status
 * endpoint. Disconnecting a linked account lives on the tile's trash icon
 * (see whatsappUnlinkMutation in the gallery), not here — this component owns
 * only the connect and reconnect flow.
 */
type WhatsappLinkStage = "pairing" | "qr" | "connecting" | "expired" | "preparing" | "unavailable";

/**
 * Which body the connect dialog shows. Once a code was shown (QR or pairing)
 * and the attempt died while the dialog is still polling, the host is either
 * finishing a handshake (`connecting`) or has parked the abandoned attempt —
 * the registry reports `pending_link` (or `logged_out`) only when nothing
 * will revive the attempt, so that reads as `expired` rather than an endless
 * spinner. The expired check runs before the code branches because a dead
 * attempt trumps whatever string is still in hand — past that point any code
 * rendered would be a dead one. Pure.
 */
function whatsappLinkStage(input: {
  pairingCode: string | null;
  qr: string | null;
  hadQr: boolean;
  hadPairing: boolean;
  polling: boolean;
  starting: boolean;
  polledStatus: WhatsappSyncStatusResponse["status"] | undefined;
}): WhatsappLinkStage {
  if (
    input.polling &&
    (input.hadQr || input.hadPairing) &&
    (input.polledStatus === "pending_link" || input.polledStatus === "logged_out")
  ) {
    return "expired";
  }
  if (input.pairingCode) return "pairing";
  if (input.qr) return "qr";
  if (input.hadQr && input.polling) return "connecting";
  return input.starting || input.polling ? "preparing" : "unavailable";
}

const WhatsappConnectAction = ({
  status,
  onLinked,
}: {
  status?: WhatsappSyncStatusResponse;
  /** Runs once when a link this dialog started lands, to offer the group picker. */
  onLinked: () => void;
}) => {
  const queryClient = useQueryClient();
  const { userContext } = useCurrentUserContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  // The mutation's copy of the pairing code. Only read until the dialog's
  // first poll lands — after that the poll (which re-serves the code from the
  // host) is the authority, so a code whose socket died can't linger here.
  const [localPairingCode, setLocalPairingCode] = useState<string | null>(null);
  // Whether a QR was shown this dialog session. Once the user scans it, the
  // host stops producing QR strings while the handshake runs — so "had a QR,
  // now none, still not connected" is the connecting-in-progress state.
  const [hadQr, setHadQr] = useState(false);
  // Pairing analogue of `hadQr`: a pairing code was shown this dialog
  // session, so a collapse to `pending_link` means expired, not preparing.
  const [hadPairing, setHadPairing] = useState(false);
  // Phone-number entry (the can't-scan path): form visibility, the input's
  // value, and the last number actually submitted — kept so "Get a new code"
  // re-requests a pairing code instead of silently reverting to a QR.
  const [phoneFormOpen, setPhoneFormOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  // Deliberately NOT per-attempt state: this outlives `resetLinkSession` and a
  // dialog close, because the number is a property of the user, not of one
  // attempt. Clearing it on reopen is what used to strand a retry — the code
  // request would arrive bare, the host would resume the same failing code,
  // and "Get a new code" would fall through to re-asking for a number the user
  // had already typed.
  const [submittedPhoneNumber, setSubmittedPhoneNumber] = useState<string | null>(null);
  const connected = status?.status === "connected";
  const prefillPhone = primaryPhoneHandleValue(userContext?.user.allUserHandles);

  // Reset every per-attempt piece of state, so a reopen or a fresh start never
  // inherits the previous attempt's stage. One place to add a field to.
  // `submittedPhoneNumber` is excluded on purpose — see its declaration.
  const resetLinkSession = useCallback(() => {
    setLocalPairingCode(null);
    setHadQr(false);
    setHadPairing(false);
    setPhoneFormOpen(false);
    setPhoneInput("");
  }, []);

  // Re-arms the auto-refresh below. Held in a ref rather than reset inside
  // `resetLinkSession`, which the refresh itself calls — clearing it there
  // would let one expiry drive an endless mint loop.
  const autoRefreshedRef = useRef(false);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    autoRefreshedRef.current = false;
    resetLinkSession();
  }, [resetLinkSession]);

  const openPhoneForm = useCallback(() => {
    setPhoneInput((v) => v || prefillPhone);
    setPhoneFormOpen(true);
  }, [prefillPhone]);

  const startMutation = useMutation({
    mutationFn: startWhatsappSyncLink,
    onSuccess: (result, variables) => {
      if (result.mode === "pairing") {
        setLocalPairingCode(result.pairingCode);
        setHadPairing(true);
        // Only a request that actually carried a number knows one. A resume
        // comes back `pairing` with no `phoneNumber` in hand, and writing that
        // `null` through would erase the number a later refresh needs.
        if (variables.phoneNumber) setSubmittedPhoneNumber(variables.phoneNumber);
        setPhoneFormOpen(false);
        // Drop the pre-pairing poll snapshot: any poll data present from here
        // on is newer than this mutation, which is what lets the poll be the
        // authority over `localPairingCode` above.
        queryClient.removeQueries({ queryKey: ["whatsappSyncStatus", "poll"] });
      } else {
        setLocalPairingCode(null);
        // A phone-number submit that comes back QR means the host couldn't
        // start a pairing socket (e.g. a paired-but-reconnecting session) and
        // fell back. Leave the form so the user isn't stuck staring at it, and
        // say what happened rather than silently swapping in a QR.
        if (variables.phoneNumber) {
          setPhoneFormOpen(false);
          errorToast("Couldn't start a phone-number link. Scan the QR code instead.");
        }
      }
      if (result.mode === "already_connected") {
        void queryClient.invalidateQueries({ queryKey: ["whatsappSyncStatus"] });
        closeDialog();
      }
    },
    onError: () => errorToast("Couldn't start WhatsApp connect. Try again."),
  });

  // The dialog's fast poll uses its OWN query key so its 2.5s refetch interval
  // doesn't drive the page-level ["whatsappSyncStatus"] query (which only needs
  // to reflect the connected/pending badge). On completion both are invalidated.
  const polling = dialogOpen && !connected;
  const { data: polled } = useQuery({
    queryKey: ["whatsappSyncStatus", "poll"],
    queryFn: getWhatsappSyncStatus,
    enabled: polling,
    refetchInterval: polling ? 2500 : false,
  });

  const polledStatus = polled?.status;
  const _polledPhoneNumber = polled?.phoneNumber;
  useEffect(() => {
    if (!polling || polledStatus !== "connected") return;
    void queryClient.invalidateQueries({ queryKey: ["whatsappSyncStatus"] });
    void queryClient.invalidateQueries({ queryKey: ["whatsappConnectorStatus"] });
    void queryClient.invalidateQueries({ queryKey: ["connectors"] });
    closeDialog();
    onLinked();
  }, [polling, polledStatus, queryClient, closeDialog, onLinked]);

  // The dialog-scoped poll is authoritative once it lands, for both codes:
  // the host re-serves the in-flight string from status, and a landed poll
  // always postdates this session's mutation (see the removeQueries in
  // onSuccess). Authority requires the field to be PRESENT — an absent field
  // (host unreachable, or an old backend mid-rolling-deploy) reads as "no
  // opinion" and must not blank a code the user is mid-typing, so the test is
  // `!== undefined`, not `??`. Before the poll lands, the page snapshot bridges
  // the QR, and the mutation's own copy bridges the pairing code.
  const qr = polled?.qr !== undefined ? polled.qr : (status?.qr ?? null);
  if (polled?.qr && !hadQr) setHadQr(true);

  const pairingCode = polled?.pairingCode !== undefined ? polled.pairingCode : localPairingCode;
  if (pairingCode && !hadPairing) setHadPairing(true);

  const stage = whatsappLinkStage({
    pairingCode,
    qr,
    hadQr,
    hadPairing,
    polling,
    starting: startMutation.isPending,
    polledStatus,
  });

  // Ask the host for a genuinely new code for this same attempt. `refresh` is
  // what stops it re-serving the string that just failed; the number keeps the
  // retry on the pairing flow, and the host falls back to the one it recorded
  // at mint if this session never learned it.
  const { mutate: startLink } = startMutation;
  const requestNewCode = useCallback(() => {
    const resumePhone = hadPairing ? submittedPhoneNumber : null;
    resetLinkSession();
    startLink({ ...(resumePhone ? { phoneNumber: resumePhone } : {}), refresh: true });
  }, [hadPairing, submittedPhoneNumber, resetLinkSession, startLink]);

  // An expired pairing code is a dead end the user can do nothing useful with,
  // so replace it rather than making them find a button. Once per dialog
  // session: if the refresh itself can't produce a code, the manual button
  // below is the fallback, and retrying on a loop would mint codes nobody is
  // looking at.
  //
  // Pairing only. An expired QR is already recoverable by scanning whatever
  // the next poll renders, so auto-restarting that flow would buy nothing and
  // risk discarding a live socket on a transient `pending_link` read.
  useEffect(() => {
    if (stage !== "expired" || !hadPairing || autoRefreshedRef.current) return;
    autoRefreshedRef.current = true;
    requestNewCode();
  }, [stage, hadPairing, requestNewCode]);

  return (
    <>
      <Button
        // Solid brand, like every other Connect: this tile is hand-rolled
        // rather than going through `ConnectorConnectAction`, so it does not
        // inherit that treatment and has to state it. `reconnectButtonClass`
        // still wins for a logged-out session, which is an alert, not an offer.
        variant="primary"
        size="xs"
        className={cn(
          "h-6 px-2 text-xs shrink-0",
          status?.status === "logged_out" && reconnectButtonClass,
        )}
        disabled={startMutation.isPending}
        onClick={() => {
          // Drop the previous attempt's cached poll so its dead QR can't
          // flash (or re-latch hadQr) before the fresh session's first poll.
          queryClient.removeQueries({ queryKey: ["whatsappSyncStatus", "poll"] });
          resetLinkSession();
          setDialogOpen(true);
          // Always ask the host to link. A bare request is idempotent: if a
          // pairing is already in flight the host re-serves the same code
          // rather than discarding it, so a remount can't invalidate the code
          // the user is mid-typing — that resume decision lives on the host,
          // which knows the socket's real state, not in a client-side check
          // against a possibly-stale status snapshot.
          startMutation.mutate({});
        }}
        data-statsig-name="connectors-add-whatsapp"
      >
        {status?.status === "logged_out" ? "Reconnect" : "Connect"}
      </Button>
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setDialogOpen(true);
          } else {
            closeDialog();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect WhatsApp</DialogTitle>
            <DialogDescription>
              {stage === "pairing" || phoneFormOpen
                ? "On your phone, open WhatsApp → Settings → Linked Devices → Link a Device → Link with phone number instead, then enter the code shown here. "
                : "On your phone, open WhatsApp → Settings → Linked Devices → Link a Device, then scan this code. "}
              Linking verifies your number so the assistant can message you on WhatsApp, and your
              chats sync so it can search and read them to answer your questions. The assistant
              never sends a message from your number without your explicit approval. Every send is
              shown to you first, and you choose Send or Cancel. Unlinking deletes the synced
              archive.
            </DialogDescription>
            <p className="mt-2 text-xs text-muted-foreground">
              Linking uses an unofficial connection to WhatsApp. WhatsApp's terms don't permit
              third-party clients, so there's a small risk your personal WhatsApp account could be
              restricted or banned. Sending from your number uses the same unofficial connection and
              carries the same risk. Continue only if you accept that risk.
            </p>
          </DialogHeader>
          {phoneFormOpen ? (
            <form
              className="flex flex-col gap-3 py-4"
              onSubmit={(e) => {
                e.preventDefault();
                // Require an explicit country code. validatePhoneNumber
                // defaults a code-less number to +1, which would silently
                // mint a pairing code for the wrong number — and WhatsApp
                // linking is a heavily non-US surface.
                if (!phoneInput.trim().startsWith("+")) {
                  errorToast("Include your country code, like +44 or +55.");
                  return;
                }
                const result = validatePhoneNumber(phoneInput);
                if (result.success !== true) {
                  errorToast("Enter a valid phone number, like +1 555 123 4567.");
                  return;
                }
                startMutation.mutate({ phoneNumber: result.data });
              }}
            >
              <p className="text-sm text-muted-foreground">
                Enter your WhatsApp phone number and we'll show a code to type into WhatsApp instead
                of a QR to scan.
              </p>
              <Input
                type="tel"
                autoComplete="tel"
                aria-label="WhatsApp phone number"
                placeholder="+1 555 123 4567"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Leaving the form must not strand the dialog: if nothing
                    // is in hand (the expired-retry path opens the form with no
                    // live socket), fall back to a QR so a code still arrives.
                    setPhoneFormOpen(false);
                    if (!pairingCode && !qr) startMutation.mutate({});
                  }}
                  data-statsig-name="connectors-whatsapp-pairing-back"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={startMutation.isPending}
                  data-statsig-name="connectors-whatsapp-pairing-request"
                >
                  Get pairing code
                </Button>
              </div>
            </form>
          ) : (
            <div aria-live="polite">
              {stage === "pairing" && pairingCode && (
                <div className="flex flex-col items-center gap-3 py-4">
                  <p className="text-sm text-muted-foreground">Enter this code on your phone:</p>
                  <p className="font-mono text-2xl tracking-widest">{pairingCode}</p>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <LoaderCircle aria-hidden="true" className="size-3 animate-spin" />
                    Waiting for confirmation from your phone…
                  </p>
                </div>
              )}
              {stage === "qr" && qr && (
                <div className="flex flex-col items-center gap-3 py-2">
                  <div className="rounded-lg bg-white p-3">
                    <QRCodeSVG value={qr} size={176} />
                  </div>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <LoaderCircle aria-hidden="true" className="size-3 animate-spin" />
                    Waiting for you to scan…
                  </p>
                </div>
              )}
              {stage === "connecting" && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <LoaderCircle
                    aria-hidden="true"
                    className="size-5 animate-spin text-muted-foreground"
                  />
                  <div className="text-center">
                    <p className="text-sm font-medium">Connecting to WhatsApp…</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Hang tight. This can take up to a minute.
                    </p>
                  </div>
                </div>
              )}
              {stage === "expired" && (
                <div className="flex flex-col items-center gap-3 py-8">
                  <div className="text-center">
                    <p className="text-sm font-medium">That code expired</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      It wasn't used in time. Grab a fresh one and try again.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={startMutation.isPending}
                    onClick={requestNewCode}
                    data-statsig-name="connectors-whatsapp-new-code"
                  >
                    Get a new code
                  </Button>
                </div>
              )}
              {stage === "preparing" && (
                <div className="flex items-center justify-center gap-1.5 py-8 text-sm text-muted-foreground">
                  <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
                  Preparing your code…
                </div>
              )}
              {stage === "unavailable" && (
                <div className="flex justify-center py-8 text-sm text-muted-foreground">
                  Couldn't load a code. Reopen to retry.
                </div>
              )}
              {(stage === "qr" || stage === "preparing") && (
                <div className="flex justify-center pb-2">
                  <Button
                    variant="link"
                    size="sm"
                    className="text-xs text-muted-foreground"
                    onClick={openPhoneForm}
                    data-statsig-name="connectors-whatsapp-pairing-toggle"
                  >
                    Can't scan? Link with your phone number
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConnectorsGallery;

/**
 * Connectors on the canvas: the catalog and what is already connected, with the
 * same search, filter, and card/list tools the settings screen offers.
 *
 * The gallery keeps its own account, catalog, and disconnect-dialog state — that
 * is a lot of intertwined state and pulling a body out from under it would have
 * been a rewrite. Instead it takes `hostControls`, the same contract
 * `ArtifactLibraryBody` already had: given the three values the toolbar owns, it
 * renders the results only and leaves the chrome to whoever is showing it.
 */
export const ConnectorsCanvasView = ({
  accountSwitcher,
  leading,
}: {
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
}) => {
  const { controls, values, search } = useConnectorsGalleryControls();

  return (
    <CanvasPivotPanel
      title="Connectors"
      leading={leading}
      accountSwitcher={accountSwitcher}
      toolbar={
        <PanelToolbar
          description="Connect your tools and accounts so Okthx can work across them."
          controls={controls}
          search={{ ...search, statsigName: "connectors-canvas-search" }}
          subject="connectors"
          statsigName="connectors-canvas-filter"
        />
      }
    >
      <ConnectorsGalleryPanel hostControls={values} />
    </CanvasPivotPanel>
  );
};

/**
 * The Text Messages card's reconnect line: what lifts the block, with Okthx's
 * number linked to open that text.
 */
const TextMessagesReconnectNote = ({
  reason,
  assistantNumber,
}: {
  reason: NonNullable<TextMessagesConnector["needsAttention"]>;
  assistantNumber: string;
}) => {
  const link = (
    <TextOkthxLink
      reason={reason}
      assistantNumber={assistantNumber}
      className="font-medium underline"
      statsigName="connectors-text-messages-number"
    />
  );
  if (reason === "optedOut") {
    return <>Text START to {link} and we'll be able to text you again.</>;
  }
  return <>Send us a text at {link} and we'll be able to text you.</>;
};
